import React, { useEffect, useState } from 'react';
import { Box, Avatar, AvatarGroup, Tooltip, useTheme } from '@mui/material';
import {
  startPresenceLifecycle,
  PresenceParticipant
} from '@/services/presenceService';

interface PresenceIndicatorProps {
  sessionId: string;
  currentUserId?: string;
}

/**
 * PresenceIndicator Component
 * 
 * Displays user avatars for other users currently viewing the same session.
 * Similar to Google Docs or Notion's presence feature.
 */
export const PresenceIndicator: React.FC<PresenceIndicatorProps> = ({
  sessionId,
  currentUserId
}) => {
  const theme = useTheme();
  const [participants, setParticipants] = useState<PresenceParticipant[]>([]);

  useEffect(() => {
    if (!sessionId) {
      return;
    }

    // Start presence tracking and get cleanup function
    const cleanup = startPresenceLifecycle(
      sessionId,
      (updatedParticipants) => {
        // Filter out the current user from the display
        const others = updatedParticipants.filter(
          (p) => p.userId !== currentUserId
        );
        setParticipants(others);
      },
      10000 // Heartbeat every 10 seconds
    );

    // Cleanup when component unmounts or sessionId changes
    return cleanup;
  }, [sessionId, currentUserId]);

  // Don't render if no other users are present
  if (participants.length === 0) {
    return null;
  }

  // Helper to get initials from display name
  const getInitials = (name: string): string => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        mr: 1
      }}
    >
      <AvatarGroup
        max={4}
        sx={{
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            fontSize: '0.875rem',
            border: `2px solid ${theme.palette.background.paper}`,
            borderColor: theme.palette.divider
          }
        }}
      >
        {participants.map((participant) => (
          <Tooltip
            key={participant.userId}
            title={participant.displayName}
            placement="bottom"
          >
            <Avatar
              src={participant.avatarUrl || undefined}
              sx={{
                bgcolor: participant.avatarUrl
                  ? undefined
                  : theme.palette.primary.main,
                cursor: 'default'
              }}
            >
              {!participant.avatarUrl && getInitials(participant.displayName)}
            </Avatar>
          </Tooltip>
        ))}
      </AvatarGroup>
    </Box>
  );
};

